import React, { memo } from 'react';
import { useGameStore } from '../store/gameStore';
import { useAuth } from '../auth/useAuth';
import { Card as CardType } from '../types';

interface PlayingCardProps {
  card: CardType;
  index?: number;
  className?: string;
  quickAnimation?: boolean; // Faster animation for hit/double cards
}

const PlayingCardComponent: React.FC<PlayingCardProps> = ({ card, index = 0, className = '', quickAnimation = false }) => {
  const { effectiveProfile } = useAuth();
  const settingsCasinoName = useGameStore((state) => state.settings.casinoName);
  // Use admin's branding if set on profile, otherwise fall back to settings
  const casinoName = effectiveProfile?.casinoName || settingsCasinoName;
  const brandLabel = (casinoName || 'JK Casino').trim();
  const brandSegment = `${brandLabel.toUpperCase()} • `;
  const repeatCount = Math.max(3, Math.ceil(80 / brandSegment.length));
  const brandRingText = Array.from({ length: repeatCount }).fill(brandSegment).join('').trim();
  const ringFontSize = Math.max(7, Math.min(11, Math.round(220 / Math.max(brandRingText.length, 12))));
  const isRed = card.suit === '♥' || card.suit === '♦';
  const color = isRed ? 'text-card-red' : 'text-card-black';

  // Use quick animation for face-up cards (hit/double) or when explicitly requested
  const shouldQuickAnimate = quickAnimation || card.faceUp;
  const animationDelay = shouldQuickAnimate ? index * 0.08 : index * 0.2;
  const style = {
    animationDelay: `${animationDelay}s`,
    animationFillMode: 'backwards' as const // Hide card until animation starts
  };
  
  if (!card.faceUp) {
    return (
      <div
        className={`relative w-16 h-24 sm:w-20 sm:h-28 md:w-24 md:h-36 rounded-lg shadow-xl animate-deal ${className}`}
        style={style}
        role="img"
        aria-label={`Card back for ${brandLabel}`}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-800 to-blue-950 rounded-lg border-2 border-blue-600 overflow-hidden">
          <div className="absolute inset-2 border-2 border-blue-400 rounded opacity-50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-blue-700 rounded-full opacity-30" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg viewBox="0 0 120 120" className="w-[80%] h-[80%] text-blue-100">
              <defs>
                <path id="backCirclePath" d="M60,60 m-34,0 a34,34 0 1,1 68,0 a34,34 0 1,1 -68,0" />
              </defs>
              <circle cx="60" cy="60" r="34" fill="none" stroke="rgba(96,165,250,0.6)" strokeWidth="2" />
              <text fill="#dbeafe" fontSize={ringFontSize} fontFamily="Inter, 'Segoe UI', sans-serif" fontWeight="800">
                <textPath href="#backCirclePath" startOffset="50%" textAnchor="middle" letterSpacing="1">
                  {brandRingText}
                </textPath>
              </text>
            </svg>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className={`relative w-16 h-24 sm:w-20 sm:h-28 md:w-24 md:h-36 bg-white rounded-lg shadow-xl border-2 border-gray-300 animate-deal ${className}`}
      style={style}
    >
      <div className="absolute top-1 left-1 sm:top-2 sm:left-2">
        <div className={`text-base sm:text-lg md:text-xl font-bold ${color}`}>
          {card.rank}
        </div>
        <div className={`text-lg sm:text-xl md:text-2xl ${color} -mt-1`}>
          {card.suit}
        </div>
      </div>
      
      <div className={`absolute inset-0 flex items-center justify-center text-3xl sm:text-4xl md:text-5xl ${color}`}>
        {card.suit}
      </div>
      
      <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 rotate-180">
        <div className={`text-base sm:text-lg md:text-xl font-bold ${color}`}>
          {card.rank}
        </div>
        <div className={`text-lg sm:text-xl md:text-2xl ${color} -mt-1`}>
          {card.suit}
        </div>
      </div>
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
export const PlayingCard = memo(PlayingCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.card.id === nextProps.card.id &&
    prevProps.card.faceUp === nextProps.card.faceUp &&
    prevProps.index === nextProps.index &&
    prevProps.className === nextProps.className
  );
});
